"use client";

import { Icon, ospThemes } from "@ospweb/sdk-ui";
import React, { useState } from "react";

import { OspModalType } from "@ospweb/sdk-ui/src/modal/types";
import { cn } from "utils";
import { createCallable } from "react-call";
import log from "logbeacon";
import { EdgenModal } from "../EdgenModal";
import Translations from "@translate/components/EditPortfolioModal/EditPortfolioModal";
import { I18nUtil } from "@utils/i18n";

const I18n = I18nUtil.createScoped(Translations);

interface EditPortfolioModalProps {
  /** 关闭弹窗回调 */
  onClose: () => void;
  /** 初始投资组合名称 */
  initialValue?: string;
  /** 保存成功回调 */
  onResolve?: (value: string) => void;
  /** 投资组合ID，存在则走重命名逻辑 */
  portfolioId?: string;
}

/**
 * EditPortfolioModal - 编辑投资组合弹窗内容组件
 *
 * 用于编辑投资组合名称的表单内容
 */
export const EditPortfolioModal: React.FC<EditPortfolioModalProps> = ({
  initialValue = "Portfolio",
  portfolioId,
}) => {
  const [portfolioName, setPortfolioName] = useState(initialValue);
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    if (!portfolioName.trim()) {
      log.warn("[EditPortfolioModal] Portfolio name is empty");
      return;
    }

    setLoading(true);
    try {
      // 存在 portfolioId 时走重命名逻辑，否则走新增
      // const op = portfolioId
      //   ? await favoritesCenter.renameFolder(portfolioId, portfolioName.trim())
      //   : await favoritesCenter.createFolder(portfolioName.trim());
      // if (op.success) {
      //   OspToast.success({
      //     content: portfolioId
      //       ? I18n.t("Portfolio name updated.")
      //       : I18n.t("Portfolio created successfully."),
      //   });
      //   onResolve?.(portfolioName.trim());
      //   onClose();
      // } else {
      //   OspToast.error({
      //     content: op.error || "",
      //   });
      // }
    } catch (error) {
      log.error("[EditPortfolioModal] Error saving portfolio:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-6 mt-2 md:px-0 md:pb-0 px-4 pb-4">
      {/* Description */}
      <p className="body_m text-content_secondary">
        {portfolioId
          ? I18n.t("Give it a name to keep it organized.")
          : I18n.t(
              "Build a list to track the stocks and tokens that matter to you.",
            )}
      </p>
      {/* Input Field */}
      <div className="flex flex-col gap-2 w-full">
        <label
          htmlFor="portfolio-name"
          className="body_medium_s text-content_secondary px-1"
        >
          {I18n.t("Portfolio Name")}
        </label>
        <div className="relative w-full">
          <input
            id="portfolio-name"
            type="text"
            value={portfolioName}
            onChange={(e) => setPortfolioName(e.target.value)}
            className={cn(
              "w-full h-11 px-3 rounded-2",
              "bg-background_white_overlay5 border border-solid border-border_white_transparent10",
              "body_medium_s text-content_secondary",
              "focus:outline-none focus:border-content_brand_primary/50",
              "transition-colors",
            )}
            placeholder="Portfolio"
          />
        </div>
      </div>
      {/* Save Button */}
      <button
        aria-label="Edit Portfolio Modal"
        data-openreplay-label="Edit Portfolio Modal Button"
        id="btn-edit-portfolio-modal"
        onClick={handleSave}
        className={cn(
          "button primary_button md_button w-full",
          "bg-background_white text-content_inverse_primary",
          "button_m group",
        )}
        data-loading={loading}
      >
        <span className="group-loading:hidden">{I18n.t("Save")}</span>
        <Icon
          name="ic_loading2"
          size={20}
          color={ospThemes.colors.content_inverse_primary}
          className={cn("hidden animate-spin group-loading:block")}
        />
      </button>
    </div>
  );
};

// 定义可创建类型的 Props
type EditPortfolioModalCreateAbleProps = {
  /** 初始投资组合名称 */
  initialValue?: string;
  /** 自定义弹窗属性 */
  modalProps?: OspModalType;
  /** 投资组合ID, 用于编辑投资组合 */
  portfolioId?: string;
};

/**
 * EditPortfolioModalCreateAble - 可编程调用的编辑投资组合弹窗
 *
 * 使用 EdgenModal 与 react-call 创建的异步函数形式，支持 Promise 返回结果
 *
 * @example
 * ```tsx
 * const portfolioName = await EditPortfolioModalCreateAble({
 *   initialValue: "My Portfolio"
 * });
 * if (portfolioName) {
 *   console.log('Saved:', portfolioName);
 * }
 * ```
 */
export const EditPortfolioModalCreateAble = createCallable<
  EditPortfolioModalCreateAbleProps,
  string | undefined
>(({ call, initialValue = "Portfolio", portfolioId, modalProps }) => {
  const handleClose = () => {
    call.end(undefined);
  };

  const {
    onCancel: modalOnCancel,
    onClose: _modalOnClose,
    ...restModalProps
  } = modalProps ?? {};

  return (
    <EdgenModal
      open={true}
      onClose={handleClose}
      containerClassName="max-w-full !md:w-[352px] overflow-visible"
      contentStyle={{
        backgroundColor: ospThemes.colors.background_solid_secondary,
        width: "100%",
        padding: 24,
        border: "none",
      }}
      closeStyle={{
        backgroundColor: ospThemes.colors.background_solid_secondary,
        top: "-6px",
        right: "-6px",
        fontSize: "16px",
      }}
      title={
        portfolioId ? I18n.t("Edit Portfolio") : I18n.t("Create Portfolio")
      }
      titleClassName="headline_regular_s text-content_primary bg-background_solid_secondary"
      styles={{
        wrapper: {
          zIndex: 1000,
        },
      }}
      destroyOnClose={true}
      onCancel={(event) => {
        modalOnCancel?.(event);
        handleClose();
      }}
      {...restModalProps}
    >
      <EditPortfolioModal
        initialValue={initialValue}
        portfolioId={portfolioId}
        onResolve={(value) => call.end(value)}
        onClose={handleClose}
      />
    </EdgenModal>
  );
});
